import _ from 'underscore';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLoggerContext } from '@bingads-webui-cc-react/instrumentation-base';
import { NoValidationRuleIndex, Rules } from '../validation';
import { logsScenario } from '../constants';

const ValidationState = {
  Idle: 'Idle',
  Trigger: 'Trigger',
  InProgress: 'InProgress',
  Done: 'Done',
};
const ValidatingStates = [ValidationState.Trigger, ValidationState.InProgress];

export const useTriggerValidation = ({
  getTriggerValidationFunction,
  moveForward,
  validationRules,
  constructAblInfoData,
  isReadonly,
  isUseSameLocationChecked,
}) => {
  const triggerValidationArgs = useRef(null);
  const validationSuccess = useRef(null);
  const [validationState, setValidationState] = useState(ValidationState.Idle);
  const [currentValidationIndex, setCurrentValidationIndex] = useState(NoValidationRuleIndex);
  const singleValidationRuleName = _.get(triggerValidationArgs.current, 'ruleName', null);
  const currentValidationRuleName = useMemo(() => (
    currentValidationIndex === NoValidationRuleIndex ? Rules.Unknown : validationRules[currentValidationIndex].name
  ), [currentValidationIndex, validationRules]);
  const { trace } = useLoggerContext();

  const getValidationIndexByRuleName = useCallback(() => {
    if (!singleValidationRuleName) {
      return 0; // start from first rule
    }
    const indexByRuleName = _.findIndex(validationRules, validationRule => validationRule.name === singleValidationRuleName);
    return indexByRuleName !== -1 ? indexByRuleName : NoValidationRuleIndex;
  }, [singleValidationRuleName, validationRules]);

  const moveForwardAfterValidation = useCallback(() => {
    setCurrentValidationIndex(NoValidationRuleIndex);
    const moveForwardData = {
      ...constructAblInfoData(),
      triggerValidationArgs: triggerValidationArgs.current,
      ruleName: currentValidationRuleName,
      success: validationSuccess.current,
    };
    moveForward(moveForwardData);
    trace({
      scenarioName: logsScenario.MoveForwardAfterValidation,
      message: { ...moveForwardData, isUseSameLocationChecked },
    });
  }, [constructAblInfoData, currentValidationRuleName, isUseSameLocationChecked, moveForward, trace]);

  const setValidationDone = useCallback(({ success }) => {
    validationSuccess.current = success;
    setValidationState(ValidationState.Done);
  }, []);

  const onContinueValidation = useCallback(() => {
    if (!singleValidationRuleName && currentValidationIndex < validationRules.length - 1) {
      setCurrentValidationIndex(currentValidationIndex + 1);
    } else {
      setValidationDone({ success: true });
    }
  }, [currentValidationIndex, setValidationDone, singleValidationRuleName, validationRules.length]);

  const onCancelValidation = useCallback(() => {
    setValidationDone({ success: false });
  }, [setValidationDone]);

  const startValidation = useCallback(() => {
    if (isReadonly) {
      setValidationDone({ success: true });
      return;
    }

    const indexByRuleName = getValidationIndexByRuleName();
    if (indexByRuleName === NoValidationRuleIndex) {
      onCancelValidation();
    } else {
      setCurrentValidationIndex(indexByRuleName);
    }
  }, [isReadonly, getValidationIndexByRuleName, setValidationDone, onCancelValidation]);

  // Using a state machine to control the validation process.
  //   - Block triggerValidation during validating
  //   - Make sure all side effects are done when switching to a new state
  const triggerValidation = useCallback((params) => {
    if (_.contains(ValidatingStates, validationState)) {
      return false;
    }

    triggerValidationArgs.current = params || {};
    setValidationState(ValidationState.Trigger);
    return true;
  }, [validationState]);

  useEffect(() => {
    getTriggerValidationFunction(triggerValidation);
  }, [getTriggerValidationFunction, triggerValidation]);

  useEffect(() => {
    switch (validationState) {
      case ValidationState.Trigger:
        setValidationState(ValidationState.InProgress);
        startValidation();
        break;
      case ValidationState.Done:
        setValidationState(ValidationState.Idle);
        moveForwardAfterValidation();
        break;
      default:
        break;
    }
  }, [moveForwardAfterValidation, startValidation, validationState]);

  return {
    currentValidationIndex,
    currentValidationRuleName,
    onContinueValidation,
    onCancelValidation,
  };
};
